import { Col, Dropdown, Input, Popconfirm, Row, Tabs, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, MoreOutlined, PictureOutlined } from '@ant-design/icons';
import { pdfjs } from 'react-pdf';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  calculateExactAge,
  getAccessToken,
  getUserDetails,
  makePostCall,
  makeGetCall,
} from '../../utils/helper';
import { openViewer } from '../../utils/tableActionHandlers';
import './view-notes.scss';
import PdfViewer from '../../components/PdfViewer';
const moment = require('moment');

// Heuristic for billing-only line items (e.g. "PORTABLE MACHINE CHARGES",
// consumables) that never get scanned. Interim until HIS billing data is
// surfaced — see project_view_notes_redesign Phase 2.
const BILLING_DESC_RE = /charge|consumable/i;

// Classify a history order into one of three display states.
const getOrderState = (order) => {
  const isScanned = !!(order?.ps_study_uid || order?.ps_study_dt_tm);
  const isBilling = !isScanned && BILLING_DESC_RE.test(order?.po_diag_desc || '');
  return { isScanned, isBilling };
};

const iconStyle = {
  fontSize: '18px',
  cursor: 'pointer',
  color: '#595959',
};

const ALLOWED_FILE_TYPES = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};

const getNormalizedNoteId = (noteId: string | number | null | undefined) =>
  noteId === null || noteId === undefined ? null : String(noteId);

const areSameNote = (firstNoteId: string | number | null | undefined, secondNoteId: string | number | null | undefined) =>
  getNormalizedNoteId(firstNoteId) === getNormalizedNoteId(secondNoteId);

const areSameOrder = (firstOrderId: string | number | null | undefined, secondOrderId: string | number | null | undefined) =>
  getNormalizedNoteId(firstOrderId) === getNormalizedNoteId(secondOrderId);

const formatHistoryDate = (value: string | null | undefined) =>
  value ? moment(value).format('DD-MM-YYYY') : null;

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker-min.js';

const ViewNotes = ({ viewNotesModal, handleNoteSelection, selectedNote, isEditMode, onEditNoteStateChange, onSaveNoteRegister }) => {
  const [prevNotes, setPrevNotes] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [mimeType, setMimeType] = useState('');
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | number | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [hideCharges, setHideCharges] = useState(false);

  const { TextArea } = Input;

  const { details } = viewNotesModal;
  const { pacs_order } = details;
  const { patient } = pacs_order;

  const userDetails = getUserDetails();
  const user_id = userDetails?.username;

  // Launch the DICOM viewer for a history order's study (Phase 2 #1). Reuses the
  // shared openViewer handler (permission check + dual-monitor launch).
  const handleViewImages = (order, event) => {
    event?.stopPropagation();
    if (!order?.ps_study_uid) {
      message.info('No images available for this order');
      return;
    }
    openViewer(
      {
        ps_study_uid: order.ps_study_uid,
        pacs_order: {
          po_modality: order.po_modality,
          po_body_part: order.po_body_part,
          po_diag_desc: order.po_diag_desc,
        },
      },
      userDetails
    );
  };

  // The order whose notes are currently shown. Defaults to the order the modal
  // was opened from; clicking a card in the Medical History list switches it.
  const [selectedOrderId, setSelectedOrderId] = useState<string | number | null>(pacs_order?.pacs_ord_id ?? null);

  // Resolve the selected order to a full object — prefer the row from the
  // patient-wide history (richer fields), fall back to the modal's order.
  const selectedOrder = useMemo(() => {
    const fromHistory = orderHistory.find(order => areSameOrder(order?.pacs_ord_id, selectedOrderId));
    return fromHistory || pacs_order || null;
  }, [orderHistory, selectedOrderId, pacs_order]);

  const fetchDocumentContent = useCallback(async (noteId: string | number) => {
    setLoadingPdf(true);
    try {
      const res = await makeGetCall(`/fetch-note-file-by-id?note_id=${noteId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        responseType: 'arraybuffer',
      });
      setPdfData(res.data);
    } catch (error) {
      console.error('Error loading PDF', error);
      setPdfData(null);
    } finally {
      setLoadingPdf(false);
    }
  }, []);

  // Patient-wide list of orders for the left "Medical History" column.
  const fetchOrderHistory = useCallback(async () => {
    const pin = patient?.pat_pin;
    if (!pin) {
      setOrderHistory([]);
      return;
    }

    setLoadingOrders(true);
    try {
      const res = await makePostCall('/pat-order-history', { pin, user_id });
      setOrderHistory(res.data?.data || []);
    } catch (e) {
      console.log('Error fetching order history', e);
      setOrderHistory([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [patient?.pat_pin, user_id]);

  useEffect(() => {
    fetchOrderHistory();
  }, [fetchOrderHistory]);

  const fetchPrevNotes = useCallback(async () => {
    const ordNo = selectedOrder?.pacs_ord_id;
    if (!ordNo) {
      setPrevNotes([]);
      return;
    }

    const payload = {
      user_id,
      pin: patient?.pat_pin,
      order_id: selectedOrder?.po_his_ord_no || selectedOrder?.his_ord_no,
      ord_no: ordNo,
    };

    try {
      const res = await makePostCall('/pat-prev-notes', payload);
      const notesData = res.data?.data || [];
      setPrevNotes(notesData);

      const flatNotes = notesData.flatMap(group => group?.rows || []);
      const currentSelectedId = selectedNote?.rn_id;
      const currentSelectedKey = currentSelectedId === null || currentSelectedId === undefined ? null : String(currentSelectedId);
      const matchedCurrent = currentSelectedKey
        ? flatNotes.find(note => String(note?.rn_id) === currentSelectedKey)
        : null;
      const nextSelection = matchedCurrent || flatNotes[0] || null;
      const nextSelectionKey =
        nextSelection?.rn_id === null || nextSelection?.rn_id === undefined
          ? null
          : String(nextSelection?.rn_id);

      const isSelectionChanged = nextSelectionKey !== currentSelectedKey;
      const isCurrentSelectionContentStale =
        !!matchedCurrent &&
        (matchedCurrent?.rn_notes || '') !== (selectedNote?.rn_notes || '');

      if (isSelectionChanged || isCurrentSelectionContentStale) {
        handleNoteSelection(nextSelection, viewNotesModal?.details);
      }
    } catch (e) {
      console.log('Error', e);
      setPrevNotes([]);
    }
  }, [selectedOrder, viewNotesModal, user_id, patient?.pat_pin, selectedNote?.rn_id, selectedNote?.rn_notes, handleNoteSelection]);

  useEffect(() => {
    fetchPrevNotes();
    // Re-fetch whenever the selected order changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId]);

  useEffect(() => {
    const mtype = selectedNote?.rn_upload_path?.split('.').pop();

    const mymime = ALLOWED_FILE_TYPES[mtype];
    setMimeType(mymime);

    if (selectedNote?.rn_upload_path && selectedNote?.rn_upload_type !== 'notes' && selectedNote?.rn_id) {
      fetchDocumentContent(selectedNote.rn_id);
    } else {
      setPdfData(null);
    }

    if (selectedNote?.rn_upload_type === 'notes') {
      setEditedNoteText(selectedNote?.rn_notes || '');
    } else {
      setEditedNoteText('');
    }

    setEditingNoteId(null);
    if (onEditNoteStateChange) {
      onEditNoteStateChange(false);
    }
  }, [selectedNote, fetchDocumentContent, onEditNoteStateChange]);

  const onSelectOrder = (order) => {
    if (areSameOrder(order?.pacs_ord_id, selectedOrderId)) {
      return;
    }
    // Switching orders abandons any in-progress edit.
    setEditingNoteId(null);
    if (onEditNoteStateChange) {
      onEditNoteStateChange(false);
    }
    setSelectedOrderId(order?.pacs_ord_id ?? null);
  };

  const onEditNote = (note, details) => {
    console.log('Edit Note', note, details);

    if (note?.rn_upload_type !== 'notes') {
      message.warning('Only text notes can be edited');
      return;
    }

    setEditingNoteId(note?.rn_id);
    setEditedNoteText(note?.rn_notes || '');
    if (onEditNoteStateChange) {
      onEditNoteStateChange(true);
    }

  };

  const saveEditedNote = useCallback(async () => {
    if (!editingNoteId || selectedNote?.rn_upload_type !== 'notes') {
      message.info('No note selected for editing');
      return false;
    }

    const updatedText = editedNoteText?.trim();
    if (!updatedText) {
      message.warning('Note cannot be empty');
      return false;
    }

    setSavingNote(true);
    try {
      if (areSameNote(selectedNote?.rn_id, editingNoteId)) {
        handleNoteSelection(
          {
            ...selectedNote,
            rn_notes: updatedText,
          },
          viewNotesModal?.details
        );
      }

     await makePostCall('/save-note', {
        note_id: editingNoteId,
        notes: updatedText,
        user_id,
      });
      message.success('Note saved successfully');
      await fetchPrevNotes();
      setEditingNoteId(null);
      if (onEditNoteStateChange) {
        onEditNoteStateChange(false);
      }
      return true;
    } catch (error) {
      console.log('Error saving note', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save note';
      message.error(errorMessage);
      return false;
    } finally {
      setSavingNote(false);
    }
  }, [
    editingNoteId,
    editedNoteText,
    selectedNote,
    user_id,
    onEditNoteStateChange,
    handleNoteSelection,
    viewNotesModal,
    fetchPrevNotes,
  ]);

  // Keep the latest saveEditedNote in a ref and register a STABLE wrapper with the
  // parent only once. Previously this effect ran on every keystroke (saveEditedNote
  // depends on editedNoteText), calling the parent's setSaveNoteHandler each time and
  // re-rendering the whole parent page per character — the source of the typing lag.
  const saveEditedNoteRef = useRef(saveEditedNote);
  useEffect(() => {
    saveEditedNoteRef.current = saveEditedNote;
  }, [saveEditedNote]);

  useEffect(() => {
    if (onSaveNoteRegister) {
      onSaveNoteRegister(() => () => saveEditedNoteRef.current());
    }
  }, [onSaveNoteRegister]);

  useEffect(() => {
    if (isEditMode === false && editingNoteId !== null) {
      setEditingNoteId(null);
      if (selectedNote?.rn_upload_type === 'notes') {
        setEditedNoteText(selectedNote?.rn_notes || '');
      } else {
        setEditedNoteText('');
      }
    }
  }, [isEditMode, editingNoteId, selectedNote]);

  const onDeleteNote = async (note, details) => {
    try {
      const payload = {
        note_id: note?.rn_id,
      };

      await makePostCall('/delete-note', payload);
      message.success('Note deleted successfully');
      fetchPrevNotes();
      // keep the per-order note counts in the history list fresh
      fetchOrderHistory();
    } catch (error) {
      console.log('Error deleting note', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete note';
      message.error(errorMessage);
    }
  };

  const renderPatientBanner = () => {
    const age = patient?.pat_dob ? calculateExactAge(patient?.pat_dob) : null;
    const dobGender = `${patient?.pat_dob || '—'} / ${patient?.pat_sex || '—'}${age ? ` · ${age}` : ''}`;

    // Column-major to mirror the reference layout (4 columns, 2 rows).
    const fields = [
      { label: 'Patient Name', value: patient?.pat_name },
      { label: 'Patient ID', value: patient?.pat_pin },
      { label: 'Ref. Phys.', value: selectedOrder?.po_ref_doc },
      { label: 'Practice Phone', value: null },
      { label: 'DOB / Gender', value: dobGender },
      { label: 'Accession No', value: selectedOrder?.po_acc_no },
      { label: 'Group Practice', value: null },
      { label: 'Practice Fax', value: null },
    ];

    return (
      <div className="patient-card">
        {fields.map(field => (
          <div className="pc-field" key={field.label}>
            <span className="pc-label">{field.label}</span>
            <span className="pc-value">{field.value || '—'}</span>
          </div>
        ))}
      </div>
    );
  };

  const chargeCount = useMemo(
    () => orderHistory.filter(order => getOrderState(order).isBilling).length,
    [orderHistory]
  );

  const visibleOrders = useMemo(
    () => (hideCharges ? orderHistory.filter(order => !getOrderState(order).isBilling) : orderHistory),
    [orderHistory, hideCharges]
  );

  const renderMedicalHistory = () => (
    <div className="vn-panel">
      <div className="vn-panel-head">
        <span>Medical History</span>
        <span className="vn-head-right">
          {chargeCount > 0 && (
            <span
              className={`mh-toggle ${hideCharges ? 'active' : ''}`}
              onClick={() => setHideCharges(prev => !prev)}
              title={hideCharges ? 'Show billing/charge entries' : 'Hide billing/charge entries'}
            >
              {hideCharges ? 'Show charges' : 'Hide charges'}
            </span>
          )}
          {visibleOrders.length > 0 && <span className="vn-count">{visibleOrders.length}</span>}
        </span>
      </div>
      <div className="vn-panel-body mh-body">
        {loadingOrders && <div className="vn-empty">Loading…</div>}
        {!loadingOrders && visibleOrders.length === 0 && (
          <div className="vn-empty">No previous orders found</div>
        )}
        {visibleOrders.map(order => {
          const { isScanned, isBilling } = getOrderState(order);
          const studyDate = formatHistoryDate(order?.ps_study_dt_tm || order?.po_req_time);
          const reportDate = formatHistoryDate(order?.ps_scan_received_at);
          return (
            <div
              key={order?.pacs_ord_id}
              className={`mh-item ${isBilling ? 'billing' : ''} ${areSameOrder(order?.pacs_ord_id, selectedOrderId) ? 'selected' : ''}`}
              onClick={() => onSelectOrder(order)}
              title={order?.po_diag_desc || ''}
            >
              <div className="mh-top">
                <span className="mh-date">
                  {studyDate || '—'}
                  {isScanned && reportDate ? <span className="mh-reportdate"> ({reportDate})</span> : null}
                </span>
                <span className="mh-chips">
                  {order?.po_modality ? <span className="mh-mod">{order.po_modality}</span> : null}
                  {order?.note_count > 0 ? <span className="mh-nc" title={`${order.note_count} note(s)`}>{order.note_count}</span> : null}
                  {isScanned ? (
                    <Tooltip title="View images">
                      <span
                        className="mh-view"
                        onClick={event => handleViewImages(order, event)}
                      >
                        <PictureOutlined />
                      </span>
                    </Tooltip>
                  ) : null}
                </span>
              </div>
              <div className="mh-desc">{order?.po_diag_desc || '—'}</div>
              {!isScanned ? (
                <span className={`mh-flag ${isBilling ? 'billing' : 'pending'}`}>
                  {isBilling ? 'Charges' : 'Not scanned'}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderNotesList = () => (
    <div className="vn-panel">
      <div className="vn-panel-head"><span>Order Notes</span></div>
      <div className="vn-panel-body notes-list">
      {prevNotes.length === 0 && (
        <div className="vn-empty">No notes for this order</div>
      )}
      {prevNotes?.map(dateNote => (
        <div key={dateNote.creationdate}>
          <div className="notes-date">{dateNote.creationdate}</div>
          {dateNote?.rows?.map(note => (
            <div
              key={note.rn_id}
              className={`notes-list-item ${areSameNote(note.rn_id, selectedNote?.rn_id) ? 'selected' : ''}`}
              onClick={() => {
                if (!areSameNote(note.rn_id, selectedNote?.rn_id)) {
                  handleNoteSelection(note, viewNotesModal?.details);
                }
              }}
            >
              <div className="nl-row">
                <div className="nl-info">
                  <span className="nl-type">{note.rn_upload_type?.toUpperCase()}</span>
                  {note.rn_file_name ? <span className="nl-name" title={note.rn_file_name}>{note.rn_file_name}</span> : null}
                  <span className="nl-time">{moment(note.createdAt).format('DD/MM/YY HH:mm')}</span>
                </div>
                <Dropdown
                  overlayClassName="view-notes-options-menu"
                  placement="bottomRight"
                  overlayStyle={{ marginTop: '-6px' }}
                  menu={{
                    items: [
                      {
                        type: 'group',
                        label: 'Options',
                        children: [
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: <EditOutlined />,
                            disabled: note?.rn_upload_type !== 'notes',
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: <DeleteOutlined />,
                          },
                        ],
                      },
                    ],
                    onClick: ({ key, domEvent }) => {
                      domEvent.preventDefault();
                      domEvent.stopPropagation();

                      if (key === 'edit' && onEditNote) {
                        onEditNote(note, viewNotesModal?.details);
                      }

                      if (key === 'delete' && onDeleteNote) {
                        setPendingDeleteNoteId(note?.rn_id);
                      }
                    },
                  }}
                  trigger={['click']}
                >
                  <Popconfirm
                    title="Delete note?"
                    description="This action will delete the selected note."
                    okText="Yes"
                    cancelText="No"
                    okType="danger"
                    open={pendingDeleteNoteId === note?.rn_id}
                    onConfirm={event => {
                      event?.preventDefault();
                      event?.stopPropagation();
                      setPendingDeleteNoteId(null);
                      onDeleteNote(note, viewNotesModal?.details);
                    }}
                    onCancel={event => {
                      event?.preventDefault();
                      event?.stopPropagation();
                      setPendingDeleteNoteId(null);
                    }}
                    onOpenChange={open => {
                      if (!open && pendingDeleteNoteId === note?.rn_id) {
                        setPendingDeleteNoteId(null);
                      }
                    }}
                    overlayStyle={{ width: 300 }}
                    placement="topRight"
                  >
                    <span
                      className="action-icon-button"
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!areSameNote(note.rn_id, selectedNote?.rn_id)) {
                          handleNoteSelection(note, viewNotesModal?.details);
                        }
                      }}
                    >
                      <MoreOutlined style={iconStyle} />
                    </span>
                  </Popconfirm>
                </Dropdown>
              </div>
            </div>
          ))}
        </div>
      ))}
      </div>
    </div>
  );

  const renderNoteDetail = () => (
    <div className="vn-panel">
      <div className="vn-panel-head"><span>Preview</span></div>
      <div className="vn-panel-body notes-detail">
      {selectedNote?.rn_upload_type === 'notes' ? (
        areSameNote(editingNoteId, selectedNote?.rn_id) ? (
          <div style={{ padding: '5px' }}>
            <TextArea
              value={editedNoteText}
              rows={12}
              onChange={event => {
                setEditedNoteText(event.target.value);
              }}
              disabled={savingNote}
            />
          </div>
        ) : (
          <div style={{ padding: '5px' }}>{selectedNote?.rn_notes}</div>
        )
      ) : (
        selectedNote?.rn_upload_path && (
          <PdfViewer
            pdfArrayBuffer={pdfData}
            refreshing={loadingPdf}
            patient_id={patient?.pat_id}
            order_id={selectedOrder?.pacs_ord_id}
            patientData={{
              patient: patient,
              ps_study_dt_tm: selectedNote?.createdAt,
            }}
            onReportUpdated
            hideCopyButton={true}
            hideWhatsappButton={true}
            mimeType={mimeType}
          />
        )
      )}
      </div>
    </div>
  );

  const risNotesTab = (
    <>
      {renderPatientBanner()}
      <Row gutter={10} className="vn-columns">
        <Col span={5}>{renderMedicalHistory()}</Col>
        <Col span={5}>{renderNotesList()}</Col>
        <Col span={14}>{renderNoteDetail()}</Col>
      </Row>
    </>
  );

  const hisNotesTab = (
    <div className="his-notes-placeholder">
      <div className="his-notes-placeholder-title">HIS Notes</div>
      <div className="his-notes-placeholder-text">
        HIS integration is not available yet. Hospital information system notes will appear here once connected.
      </div>
    </div>
  );

  return (
    <div className="view-notes-container">
      <div className="notes-header">MEDICAL NOTES</div>
      <Tabs
        defaultActiveKey="ris"
        items={[
          { key: 'his', label: 'HIS Notes', children: hisNotesTab },
          { key: 'ris', label: 'RIS Notes', children: risNotesTab },
        ]}
      />
    </div>
  );
};

export default ViewNotes;
